import { openSignatureRequestPopup } from '@stacks/connect';

function stableStringify(obj) {
  if (obj === null || typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) return `[${obj.map((x) => stableStringify(x)).join(',')}]`;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

async function sha256Hex(str) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function requestSignature(userSession, message) {
  return new Promise((resolve, reject) => {
    openSignatureRequestPopup({
      message,
      network: 'mainnet',
      userSession,
      onFinish: (data) => resolve(data),
      onCancel: () => reject(new Error('Signature cancelled'))
    });
  });
}

async function pinFile(file) {
  const pinataApiUrl = 'https://api.pinata.cloud/pinning/pinFileToIPFS';
  const pinataApiKey = process.env.PINATA_API_KEY;
  const pinataSecretApiKey = process.env.PINATA_SECRET_KEY;
  if (!pinataApiKey || !pinataSecretApiKey) {
    throw new Error('Pinata keys missing (PINATA_API_KEY / PINATA_SECRET_KEY)');
  }
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(pinataApiUrl, {
    method: 'POST',
    headers: {
      pinata_api_key: pinataApiKey,
      pinata_secret_api_key: pinataSecretApiKey
    },
    body: formData
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`Pinata upload failed: ${res.status} ${t}`);
  }
  const { IpfsHash } = await res.json();
  return IpfsHash;
}

async function callCreator(router, action, bodyExtra) {
  const stx = router.auth.getCurrentAddress();
  if (!stx) throw new Error('Wallet not connected');
  const ts = Date.now();
  let message;

  if (action === 'list') {
    message = `gated-album:list:${ts}`;
  } else if (action === 'manage') {
    const hash = await sha256Hex(stableStringify(bodyExtra.album));
    message = `gated-album:manage:${ts}:${hash}`;
  } else if (action === 'publish') {
    message = `gated-album:publish:${ts}:${bodyExtra.creator_slug}|${bodyExtra.album_slug}`;
  } else if (action === 'draft') {
    message = `gated-album:draft:${ts}:${bodyExtra.creator_slug}|${bodyExtra.album_slug}`;
  } else {
    throw new Error('bad action');
  }

  const { signature, publicKey } = await requestSignature(router.auth.userSession, message);
  const res = await fetch('/api/album/creator', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action,
      stxAddress: stx,
      message,
      signature,
      publicKey,
      ...bodyExtra
    })
  });
  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(text || 'Bad response');
  }
  if (!res.ok) {
    throw new Error(data.error || res.statusText);
  }
  return data;
}

function slugify(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function cidFromUrl(url) {
  if (!url) return '';
  const m = String(url).match(/ipfs\/([^/?]+)/);
  return m ? m[1] : '';
}

const GATEWAY = 'https://nftstorage.link/ipfs/';

function buildLocalPreviewData(albumPayload) {
  const ip = (cid) => (cid ? `${GATEWAY}${String(cid).replace(/^ipfs:\/\//, '')}` : '');
  return {
    album: {
      title: albumPayload.title,
      description: albumPayload.description,
      cover_url: ip(albumPayload.cover_cid),
      collector_note: albumPayload.collector_note || '',
      contract_address: albumPayload.contract_address || '',
      contract_name: albumPayload.contract_name || '',
      max_supply: albumPayload.max_supply,
      mint_count: 0,
      nft_asset_name: albumPayload.nft_asset_name || 'founding-album-pass'
    },
    tracks: (albumPayload.tracks || []).map((t) => ({
      title: t.title,
      duration_seconds: t.duration_seconds,
      audio_url: ip(t.audio_cid),
      art_url: t.art_cid ? ip(t.art_cid) : ''
    }))
  };
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function renderAlbumsSection(router) {
  const contentSection = router.mainContent?.querySelector('.pages-section');
  if (!contentSection) return;

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://gated.so';

  contentSection.innerHTML = `
    <div class="section-header albums-dash-header">
      <div class="section-header-text">
        <div class="section-header-title">Albums</div>
        <div class="section-header-subtitle">Path URL: ${origin}/creator-slug/album-slug (save draft, then publish)</div>
      </div>
      <button type="button" class="create-btn" id="album-new-btn">New album</button>
    </div>
    <div id="albums-dash-body" class="albums-dash-body">
      <div id="albums-list-wrap" class="albums-list-wrap">Loading…</div>
      <div id="album-editor-wrap" class="album-editor-wrap" style="display:none;"></div>
      <div id="album-preview-wrap" class="album-preview-wrap" style="display:none;">
        <button type="button" class="btn-secondary" id="album-preview-close">Close preview</button>
        <div id="album-preview-mount" class="album-preview-mount"></div>
      </div>
    </div>
  `;

  const loadList = async () => {
    const wrap = contentSection.querySelector('#albums-list-wrap');
    try {
      const { albums } = await callCreator(router, 'list', {});
      if (!albums?.length) {
        wrap.innerHTML = '<p class="albums-empty">No albums yet. Click New album.</p>';
        return;
      }
      wrap.innerHTML = `
        <ul class="albums-dash-list">
          ${albums
            .map(
              (a) => `
            <li class="albums-dash-item" data-id="${a.id}">
              <div>
                <strong>${escapeHtml(a.title || '(untitled)')}</strong>
                <span class="albums-dash-slugs">${escapeHtml(a.creator_slug)}/${escapeHtml(a.album_slug)}</span>
                <span class="albums-dash-status">${escapeHtml(a.status)}</span>
              </div>
              <div class="albums-dash-actions">
                <button type="button" class="btn-small album-edit-btn" data-creator="${escapeHtml(a.creator_slug)}" data-slug="${escapeHtml(a.album_slug)}">Edit</button>
                <button type="button" class="btn-small album-publish-btn" data-creator="${escapeHtml(a.creator_slug)}" data-slug="${escapeHtml(a.album_slug)}" ${a.status === 'published' ? 'disabled' : ''}>Publish</button>
                <a class="btn-small album-open-btn" href="/${encodeURIComponent(a.creator_slug)}/${encodeURIComponent(a.album_slug)}" target="_blank" rel="noopener" ${a.status !== 'published' ? 'style="pointer-events:none;opacity:0.4"' : ''}>Open</a>
              </div>
            </li>`
            )
            .join('')}
        </ul>`;

      wrap.querySelectorAll('.album-edit-btn').forEach((btn) => {
        btn.addEventListener('click', () =>
          openEditor(btn.dataset.creator, btn.dataset.slug)
        );
      });
      wrap.querySelectorAll('.album-publish-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
          try {
            await callCreator(router, 'publish', {
              creator_slug: btn.dataset.creator,
              album_slug: btn.dataset.slug
            });
            await loadList();
          } catch (e) {
            alert(e.message || String(e));
          }
        });
      });
    } catch (e) {
      wrap.innerHTML = `<p class="albums-error">${escapeHtml(e.message || String(e))}</p>`;
    }
  };

  const openEditor = async (creatorSlug = '', albumSlug = '') => {
    const editor = contentSection.querySelector('#album-editor-wrap');
    const listWrap = contentSection.querySelector('#albums-list-wrap');
    editor.style.display = 'block';
    listWrap.style.display = 'none';
    contentSection.querySelector('#album-new-btn').style.display = 'none';

    const state = {
      id: null,
      creator_slug: creatorSlug,
      album_slug: albumSlug,
      title: '',
      description: '',
      collector_note: '',
      contract_address: '',
      contract_name: '',
      nft_asset_name: 'founding-album-pass',
      max_supply: 50,
      cover_cid: '',
      tracks: [{ title: 'Track 1', audio_cid: '', duration_seconds: null, art_cid: '' }]
    };

    if (creatorSlug && albumSlug) {
      try {
        const data = await callCreator(router, 'draft', {
          creator_slug: creatorSlug,
          album_slug: albumSlug
        });
        state.id = data.album?.id || null;
        state.title = data.album?.title ?? '';
        state.description = data.album?.description ?? '';
        state.collector_note = data.album?.collector_note ?? '';
        state.contract_address = data.album?.contract_address ?? '';
        state.contract_name = data.album?.contract_name ?? '';
        state.max_supply = data.album?.max_supply ?? 50;
        state.cover_cid = cidFromUrl(data.album?.cover_url);
        if (data.tracks?.length) {
          state.tracks = data.tracks.map((t) => ({
            title: t.title,
            audio_cid: cidFromUrl(t.audio_url),
            duration_seconds: t.duration_seconds,
            art_cid: cidFromUrl(t.art_url)
          }));
        }
      } catch (e) {
        alert(`Could not load album: ${e.message}`);
      }
    }

    editor.innerHTML = `
      <div class="album-editor">
        <button type="button" class="btn-secondary" id="album-editor-back">Back to list</button>
        <h3>${creatorSlug && albumSlug ? 'Edit album' : 'New album'}</h3>
        <div class="album-form-grid">
          <label>Creator slug <input id="acf-creator" value="${escapeHtml(state.creator_slug)}" ${creatorSlug ? 'readonly' : ''} placeholder="e.g. my-name" /></label>
          <label>Album slug <input id="acf-album" value="${escapeHtml(state.album_slug)}" ${albumSlug ? 'readonly' : ''} placeholder="e.g. debut" /></label>
          <label>Title <input id="acf-title" value="${escapeHtml(state.title)}" /></label>
          <label>Description <textarea id="acf-desc" rows="3">${escapeHtml(state.description)}</textarea></label>
          <label>Collector note <textarea id="acf-note" rows="2">${escapeHtml(state.collector_note)}</textarea></label>
          <label>Contract address <input id="acf-ca" value="${escapeHtml(state.contract_address)}" placeholder="SP…" /></label>
          <label>Contract name <input id="acf-cn" value="${escapeHtml(state.contract_name)}" placeholder="contract-name" /></label>
          <label>Max supply <input id="acf-max" type="number" min="1" value="${state.max_supply}" /></label>
          <label>Cover image ${state.cover_cid ? `(CID: ${escapeHtml(state.cover_cid.slice(0, 12))}…)` : ''} <input id="acf-cover" type="file" accept="image/*" /></label>
          <div id="acf-tracks" class="acf-tracks"></div>
          <button type="button" class="btn-secondary" id="acf-add-track">Add track</button>
        </div>
        <div class="album-editor-actions">
          <button type="button" class="create-btn" id="acf-save">Save draft (sign)</button>
          <button type="button" class="btn-secondary" id="acf-preview">Preview</button>
        </div>
      </div>`;

    const renderTracks = () => {
      const box = editor.querySelector('#acf-tracks');
      box.innerHTML = state.tracks
        .map(
          (t, i) => `
        <div class="acf-track" data-i="${i}">
          <label>Title <input class="acf-t-title" value="${escapeHtml(t.title)}" /></label>
          <label>Audio file ${t.audio_cid ? `(have CID)` : ''} <input class="acf-t-audio" type="file" accept="audio/*" /></label>
          <label>Art (optional) <input class="acf-t-art" type="file" accept="image/*" /></label>
          <label>Duration (sec) <input class="acf-t-dur" type="number" value="${t.duration_seconds ?? ''}" /></label>
        </div>`
        )
        .join('');
    };
    renderTracks();

    editor.querySelector('#acf-add-track').addEventListener('click', () => {
      state.tracks.push({
        title: `Track ${state.tracks.length + 1}`,
        audio_cid: '',
        duration_seconds: null,
        art_cid: ''
      });
      renderTracks();
    });

    editor.querySelector('#album-editor-back').addEventListener('click', () => {
      editor.style.display = 'none';
      editor.innerHTML = '';
      listWrap.style.display = 'block';
      contentSection.querySelector('#album-new-btn').style.display = '';
      loadList();
    });

    const gatherAlbumPayload = async () => {
      const creator_slug = slugify(editor.querySelector('#acf-creator').value);
      const album_slug = slugify(editor.querySelector('#acf-album').value);
      if (!creator_slug || !album_slug) throw new Error('Creator slug and album slug required');

      let cover_cid = state.cover_cid;
      const coverInput = editor.querySelector('#acf-cover');
      if (coverInput?.files?.[0]) {
        cover_cid = await pinFile(coverInput.files[0]);
      }

      const tracks = [];
      const trackEls = editor.querySelectorAll('.acf-track');
      for (let i = 0; i < trackEls.length; i++) {
        const el = trackEls[i];
        const title = el.querySelector('.acf-t-title')?.value || `Track ${i + 1}`;
        const durVal = el.querySelector('.acf-t-dur')?.value;
        const audioFile = el.querySelector('.acf-t-audio')?.files?.[0];
        const artFile = el.querySelector('.acf-t-art')?.files?.[0];
        let audio_cid = state.tracks[i]?.audio_cid || '';
        let art_cid = state.tracks[i]?.art_cid || '';
        if (audioFile) audio_cid = await pinFile(audioFile);
        if (artFile) art_cid = await pinFile(artFile);
        if (!audio_cid) throw new Error(`Track ${i + 1}: upload audio file or load existing draft with audio`);
        tracks.push({
          position: i,
          title,
          audio_cid,
          duration_seconds: durVal ? parseInt(durVal, 10) : null,
          art_cid: art_cid || null
        });
      }

      return {
        id: state.id,
        creator_slug,
        album_slug,
        title: editor.querySelector('#acf-title').value.trim(),
        description: editor.querySelector('#acf-desc').value.trim(),
        collector_note: editor.querySelector('#acf-note').value.trim(),
        contract_address: editor.querySelector('#acf-ca').value.trim() || null,
        contract_name: editor.querySelector('#acf-cn').value.trim() || null,
        nft_asset_name: 'founding-album-pass',
        max_supply: parseInt(editor.querySelector('#acf-max').value, 10) || 50,
        cover_cid: cover_cid || null,
        tracks
      };
    };

    editor.querySelector('#acf-save').addEventListener('click', async () => {
      try {
        const album = await gatherAlbumPayload();
        const result = await callCreator(router, 'manage', { album });
        if (result.id) state.id = result.id;
        alert('Saved.');
        await loadList();
      } catch (e) {
        alert(e.message || String(e));
      }
    });

    editor.querySelector('#acf-preview').addEventListener('click', async () => {
      try {
        const { renderAlbumPageFromData } = await import('./album-page.js');
        const album = await gatherAlbumPayload();
        const data = buildLocalPreviewData(album);
        const previewWrap = contentSection.querySelector('#album-preview-wrap');
        const mount = contentSection.querySelector('#album-preview-mount');
        previewWrap.style.display = 'block';
        mount.innerHTML = '';
        await renderAlbumPageFromData(router, data, album.creator_slug, album.album_slug, {
          previewMode: true,
          mountEl: mount
        });
      } catch (e) {
        alert(e.message || String(e));
      }
    });

    contentSection.querySelector('#album-preview-close').onclick = () => {
      contentSection.querySelector('#album-preview-wrap').style.display = 'none';
    };
  };

  contentSection.querySelector('#album-new-btn').addEventListener('click', () => openEditor());

  loadList();
}
